import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Loader2, Search, Wallet, AlertTriangle, CheckCircle2, Clock, AlertCircle, Eye, Plus, ChevronLeft, ChevronRight, Calendar, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { cobrosExpensasApi, expensasApi } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { RegistrarPagoDialog } from '../dialogs/RegistrarPagoDialog';
import { CobroDetalleDialog } from '../dialogs/CobroDetalleDialog';
import { PagosInformadosBandeja } from './PagosInformadosBandeja';

const fmtMoney = (n) => {
  const v = Math.round(Number(n || 0));
  const sign = v < 0 ? '-' : '';
  return `${sign}$ ${Math.abs(v).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
};
const fmtFecha = (s) => {
  if (!s) return '—';
  const d = String(s).slice(0, 10).split('-');
  if (d.length !== 3) return s;
  return `${d[2]}/${d[1]}/${d[0]}`;
};
const fmtPeriodo = (s) => {
  if (!s) return '';
  const [y, m] = s.split('-');
  const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  return `${meses[parseInt(m, 10) - 1]} ${y}`;
};
const hoyMes = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const addMonths = (mes, delta) => {
  const [y, m] = mes.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

const ESTADOS_FILTROS = [
  { value: 'all', label: 'Todos' },
  { value: 'pendiente', label: 'Pendientes' },
  { value: 'parcial', label: 'Parciales' },
  { value: 'pagado', label: 'Pagados' },
  { value: 'pagado_tarde', label: 'Pagados tarde' },
  { value: 'vencido', label: 'Vencidos' },
  { value: 'en_revision', label: 'En revisión' },
  { value: 'con_saldo', label: 'Solo con saldo' },
];

const ESTADO_BADGE = {
  pendiente: { color: 'bg-slate-100 text-slate-700 ring-slate-200', label: 'Pendiente', icon: Clock },
  parcial: { color: 'bg-amber-50 text-amber-700 ring-amber-200', label: 'Parcial', icon: AlertCircle },
  pagado: { color: 'bg-emerald-50 text-emerald-700 ring-emerald-200', label: 'Pagado', icon: CheckCircle2 },
  pagado_tarde: { color: 'bg-blue-50 text-blue-700 ring-blue-200', label: 'Pagado tarde', icon: CheckCircle2 },
  vencido: { color: 'bg-rose-50 text-rose-700 ring-rose-200', label: 'Vencido', icon: AlertTriangle },
  en_revision: { color: 'bg-violet-50 text-violet-700 ring-violet-200', label: 'En revisión', icon: Eye },
};

function EstadoBadge({ estado }) {
  const cfg = ESTADO_BADGE[estado] || ESTADO_BADGE.pendiente;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${cfg.color}`} data-testid={`badge-estado-${estado}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function EditableDeudaCell({ it, canEdit, edificioId, periodoId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(it.deuda_anterior ?? 0));
  const [saving, setSaving] = useState(false);
  const manual = !!it.deuda_anterior_manual;

  useEffect(() => { setVal(String(it.deuda_anterior ?? 0)); }, [it.deuda_anterior]);

  const save = async () => {
    const nuevo = Math.max(0, Math.round(Number(val) || 0));
    if (nuevo === Number(it.deuda_anterior || 0) && manual === (nuevo > 0)) { setEditing(false); return; }
    setSaving(true);
    try {
      await cobrosExpensasApi.editarDeudaAnterior(edificioId, periodoId, it.unidad_id, { deuda_anterior: nuevo });
      toast.success(nuevo > 0 ? 'Deuda anterior cargada manualmente' : 'Deuda anterior vuelta a automática');
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar deuda anterior');
      setVal(String(it.deuda_anterior ?? 0));
    } finally { setSaving(false); }
  };

  const cls = Number(it.deuda_anterior) > 0 ? 'text-amber-700 font-medium' : 'text-slate-400';

  if (!canEdit) {
    return (
      <span className={cls} data-testid={`deuda-val-${it.unidad_id}`}>
        {fmtMoney(it.deuda_anterior)}
        {manual && <span title="Cargada manualmente" className="ml-1 text-[10px] text-indigo-500">✎</span>}
      </span>
    );
  }
  if (editing) {
    return (
      <input
        autoFocus type="number" min="0" value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.target.blur();
          if (e.key === 'Escape') { setVal(String(it.deuda_anterior ?? 0)); setEditing(false); }
        }}
        disabled={saving}
        data-testid={`deuda-input-${it.unidad_id}`}
        className="w-24 text-right text-xs border border-indigo-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-indigo-300"
      />
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      data-testid={`deuda-edit-${it.unidad_id}`}
      title={manual ? 'Deuda manual — click para editar (0 = volver a automática)' : 'Click para cargar deuda manual'}
      className={`group inline-flex items-center gap-1 hover:bg-indigo-50 rounded px-1 ${cls}`}
    >
      {fmtMoney(it.deuda_anterior)}
      {manual
        ? <span className="text-[10px] text-indigo-500" title="Manual">✎</span>
        : <Pencil className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100" />}
    </button>
  );
}

function KpiCard({ label, value, sub, tone = 'slate', testId }) {
  const tones = {
    slate: 'bg-white border-slate-200',
    indigo: 'bg-indigo-50/50 border-indigo-200',
    emerald: 'bg-emerald-50/50 border-emerald-200',
    rose: 'bg-rose-50/50 border-rose-200',
    amber: 'bg-amber-50/50 border-amber-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">{label}</div>
      <div className="text-base font-semibold text-slate-900 mt-0.5 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function TabCobros({ edificioId, registerHeader }) {
  const { user } = useAuth();
  const canEditDeuda = ['admin', 'administrador_consorcio'].includes(user?.rol);
  const [mes, setMes] = useState(hoyMes());
  const [periodo, setPeriodo] = useState(null);
  const [loadingPeriodo, setLoadingPeriodo] = useState(false);

  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  const [propMap, setPropMap] = useState({}); // propietario_id -> nombre
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('all');

  // Dialogs
  const [pagoDialog, setPagoDialog] = useState({ open: false, cobro: null });
  const [detalleDialog, setDetalleDialog] = useState({ open: false, unidadId: null, propNombre: '', propietarios: [] });

  // ============================================================
  // Cargar período y propietarios
  // ============================================================
  useEffect(() => {
    let cancelled = false;
    setLoadingPeriodo(true);
    setPeriodo(null);
    (async () => {
      try {
        const r = await expensasApi.getOrCreatePeriodoPorMes(edificioId, mes);
        if (!cancelled) setPeriodo(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Error al obtener período');
      } finally {
        if (!cancelled) setLoadingPeriodo(false);
      }
    })();
    return () => { cancelled = true; };
  }, [edificioId, mes]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await expensasApi.listPropietariosEdificio(edificioId);
        if (cancelled) return;
        const map = {};
        (r.data || []).forEach((p) => {
          map[p.propietario_id] = p.nombre_razon_social || p.razon_social || p.nombre || p.email || '—';
        });
        setPropMap(map);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [edificioId]);

  // ============================================================
  // Cargar items + KPIs
  // ============================================================
  const loadData = useCallback(async () => {
    if (!periodo?.periodo_id) return;
    setLoadingData(true);
    try {
      const [rList, rKpis] = await Promise.all([
        cobrosExpensasApi.list(edificioId, periodo.periodo_id),
        cobrosExpensasApi.kpis(edificioId, periodo.periodo_id),
      ]);
      setItems(rList.data?.items || []);
      setKpis(rKpis.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando cobros');
    } finally {
      setLoadingData(false);
    }
  }, [edificioId, periodo?.periodo_id]);

  useEffect(() => {
    if (periodo?.periodo_id) loadData();
  }, [periodo?.periodo_id, loadData]);

  // ============================================================
  // El selector de mes ahora vive en la fila de filtros (no en el header superior).
  // ============================================================
  const setHeader = registerHeader;
  useEffect(() => {
    if (!setHeader) return undefined;
    setHeader(null);
    return () => setHeader(null);
  }, [setHeader]);

  // ============================================================
  // Filtrado en memoria
  // ============================================================
  const filtered = useMemo(() => {
    let r = items;
    const s = search.trim().toLowerCase();
    if (s) {
      r = r.filter((it) => {
        const propNombre = propMap[it.propietario_id] || '';
        const respNombre = it.responsable_nombre || '';
        return (it.unidad_nombre || '').toLowerCase().includes(s)
          || propNombre.toLowerCase().includes(s)
          || respNombre.toLowerCase().includes(s);
      });
    }
    if (filterEstado !== 'all') {
      if (filterEstado === 'con_saldo') {
        r = r.filter((it) => Number(it.saldo_pendiente || 0) > 0);
      } else {
        r = r.filter((it) => it.estado === filterEstado);
      }
    }
    return r;
  }, [items, search, filterEstado, propMap]);

  // Conteo por filtro (para mostrar en el selector)
  const counts = useMemo(() => {
    const c = { all: items.length, con_saldo: 0 };
    items.forEach((it) => {
      c[it.estado] = (c[it.estado] || 0) + 1;
      if (Number(it.saldo_pendiente || 0) > 0) c.con_saldo += 1;
    });
    return c;
  }, [items]);

  // ============================================================
  // Acciones (solo apertura de modales — Refrescar/Sincronizar quitados del MVP)
  // ============================================================
  const onOpenPago = (cobro) => setPagoDialog({ open: true, cobro });
  const onClosePago = () => setPagoDialog({ open: false, cobro: null });
  const onOpenDetalle = (cobro) => setDetalleDialog({
    open: true,
    unidadId: cobro.unidad_id,
    propNombre: cobro.responsable_nombre || propMap[cobro.propietario_id] || '—',
    propietarios: cobro.propietarios || [],
  });
  const onCloseDetalle = () => setDetalleDialog({ open: false, unidadId: null, propNombre: '' });

  // ============================================================
  // Render
  // ============================================================
  if (loadingPeriodo || !periodo) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  const fechaUltimoPago = (it) => {
    const cps = (it.cobros_parciales || []).filter((cp) => !cp.anulado);
    if (cps.length === 0) return null;
    return cps.map((cp) => cp.fecha_pago).sort().slice(-1)[0];
  };

  const cantidadPagos = (it) => (it.cobros_parciales || []).filter((cp) => !cp.anulado).length;

  return (
    <div className="space-y-4" data-testid="tab-cobros-content">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
        <KpiCard
          label="Total a cobrar"
          value={fmtMoney(kpis?.total_a_cobrar)}
          sub={`${kpis?.cantidad_unidades || 0} unidad(es)`}
          tone="indigo"
          testId="kpi-total-cobrar"
        />
        <KpiCard
          label="Total cobrado"
          value={fmtMoney(kpis?.total_cobrado)}
          sub={`${kpis?.porcentaje_cobranza || 0}% cobranza`}
          tone="emerald"
          testId="kpi-total-cobrado"
        />
        <KpiCard
          label="Saldo pendiente"
          value={fmtMoney(kpis?.saldo_pendiente)}
          sub={`${kpis?.cantidad_vencidas || 0} vencidas`}
          tone="rose"
          testId="kpi-saldo-pendiente"
        />
        <KpiCard
          label="Pagadas"
          value={kpis?.cantidad_pagadas || 0}
          sub={`${kpis?.cantidad_parciales || 0} parciales`}
          tone="emerald"
          testId="kpi-pagadas"
        />
        <KpiCard
          label="Morosas"
          value={kpis?.cantidad_morosas || 0}
          sub={`Saldo > 0 y vencidas`}
          tone="amber"
          testId="kpi-morosas"
        />
      </div>

      {/* Bandeja de pagos informados por propietarios */}
      <PagosInformadosBandeja edificioId={edificioId} periodoId={periodo?.periodo_id} onChanged={loadData} />

      {/* Filtros (sin Refrescar/Sincronizar — la pantalla se actualiza sola) */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={() => setMes((m) => addMonths(m, -1))} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Mes anterior" data-testid="btn-mes-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-slate-200 bg-white text-xs h-8">
            <Calendar className="w-3.5 h-3.5 text-indigo-500" />
            <span className="font-medium text-slate-700 capitalize" data-testid="cobros-periodo-label">{fmtPeriodo(mes)}</span>
          </div>
          <button onClick={() => setMes((m) => addMonths(m, 1))} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Mes siguiente" data-testid="btn-mes-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input
            data-testid="input-search-cobros"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por unidad o responsable..."
            className="pl-8 h-8 text-xs"
          />
        </div>

        <Select value={filterEstado} onValueChange={setFilterEstado}>
          <SelectTrigger className="h-8 text-xs w-[180px]" data-testid="select-filter-estado">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ESTADOS_FILTROS.map((f) => (
              <SelectItem key={f.value} value={f.value} data-testid={`filter-option-${f.value}`}>
                {f.label} {counts[f.value] !== undefined ? <span className="text-slate-400 ml-1">({counts[f.value] || 0})</span> : null}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="text-xs text-slate-500" data-testid="cobros-count">
          {filtered.length} de {items.length}
        </div>

        {loadingData && (
          <div className="ml-auto flex items-center text-xs text-slate-400">
            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            Actualizando…
          </div>
        )}
      </div>

      {/* Tabla / Estados vacíos */}
      {loadingData && items.length === 0 ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        periodo?.estado !== 'aprobado' ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center" data-testid="empty-cobros-no-aprobado">
            <Wallet className="w-10 h-10 text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
            <h3 className="text-sm font-semibold text-slate-700">Sin cobros para {fmtPeriodo(mes)}</h3>
            <p className="text-xs text-slate-500 mt-2 max-w-md mx-auto">
              Primero aprobá la liquidación para generar los cobros.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-dashed border-rose-300 rounded-xl p-12 text-center" data-testid="empty-cobros-error">
            <AlertTriangle className="w-10 h-10 text-rose-400 mx-auto mb-2" strokeWidth={1.5} />
            <h3 className="text-sm font-semibold text-rose-700">No se pudieron generar los cobros del período</h3>
            <p className="text-xs text-slate-500 mt-2 max-w-md mx-auto">
              Revisá la liquidación aprobada o intentá aprobar el período nuevamente.
            </p>
          </div>
        )
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500" data-testid="no-results">
          Sin resultados para el filtro actual.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto h-[calc(100vh-265px)]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">Unidad</th>
                  <th className="px-2 py-2 text-left font-medium">Responsable de pago</th>
                  <th className="px-2 py-2 text-right font-medium">Liquidado</th>
                  <th className="px-2 py-2 text-right font-medium">Deuda ant.</th>
                  <th className="px-2 py-2 text-right font-medium">Total a cobrar</th>
                  <th className="px-2 py-2 text-right font-medium">Cobrado</th>
                  <th className="px-2 py-2 text-right font-medium">Saldo</th>
                  <th className="px-2 py-2 text-center font-medium">Vto final</th>
                  <th className="px-2 py-2 text-center font-medium">Último pago</th>
                  <th className="px-2 py-2 text-center font-medium">Estado</th>
                  <th className="px-2 py-2 text-right font-medium w-[120px]">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const propNombre = propMap[it.propietario_id] || (it.propietario_id ? '—' : 'Sin propietario');
                  const responsableNombre = it.responsable_nombre || propNombre;
                  const ult = fechaUltimoPago(it);
                  const nPagos = cantidadPagos(it);
                  const saldoPos = Number(it.saldo_pendiente || 0) > 0;
                  return (
                    <tr key={it.unidad_id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`cobro-row-${it.unidad_id}`}>
                      <td className="px-2 py-2 font-medium text-slate-800 whitespace-nowrap">
                        {it.unidad_nombre}
                        <span className="text-slate-400 ml-1 text-[10px]">{it.tipo}</span>
                      </td>
                      <td className="px-2 py-2 text-slate-700 max-w-[180px] truncate" title={responsableNombre} data-testid={`responsable-${it.unidad_id}`}>
                        {responsableNombre}
                        {it.responsable_tipo === 'propietario' && (it.propietarios_count || 0) > 1 && (
                          <span className="text-indigo-600 font-medium" title={(it.propietarios || []).map((p) => `${p.nombre || '—'} (${p.porcentaje ?? '—'}%)`).join(', ')}> +{it.propietarios_count - 1}</span>
                        )}
                        {it.responsable_tipo && (
                          <span className="block text-[9px] text-slate-400 capitalize leading-tight">{it.responsable_tipo.replace('_', ' ')}</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">{fmtMoney(it.monto_liquidado)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        <EditableDeudaCell
                          it={it}
                          canEdit={canEditDeuda}
                          edificioId={edificioId}
                          periodoId={periodo?.periodo_id}
                          onSaved={loadData}
                        />
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-medium text-slate-900">
                        <div className="flex items-center justify-end gap-1.5">
                          {it.vto_vigente && (
                            <span
                              className={`inline-flex items-center px-1 rounded text-[9px] font-bold ${
                                it.vto_vigente === 1 ? 'bg-emerald-100 text-emerald-700'
                                : it.vto_vigente === 2 ? 'bg-amber-100 text-amber-700'
                                : 'bg-rose-100 text-rose-700'
                              }`}
                              title={`Monto vigente hoy: ${it.vto_vigente}° vencimiento`}
                              data-testid={`vto-vigente-${it.unidad_id}`}
                            >
                              {it.vto_vigente}°
                            </span>
                          )}
                          {fmtMoney(it.total_a_cobrar)}
                        </div>
                      </td>
                      <td className={`px-2 py-2 text-right tabular-nums ${Number(it.total_cobrado) > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>
                        {fmtMoney(it.total_cobrado)}
                      </td>
                      <td className={`px-2 py-2 text-right tabular-nums font-semibold ${saldoPos ? 'text-rose-700' : 'text-emerald-700'}`}>
                        {fmtMoney(it.saldo_pendiente)}
                      </td>
                      <td className="px-2 py-2 text-center text-slate-600">{fmtFecha(it.fecha_vto_final)}</td>
                      <td className="px-2 py-2 text-center text-slate-600">
                        {ult ? (
                          <div className="flex flex-col items-center leading-tight">
                            <span>{fmtFecha(ult)}</span>
                            {nPagos > 1 && <span className="text-[10px] text-slate-400">{nPagos} pagos</span>}
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <EstadoBadge estado={it.estado} />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => onOpenDetalle(it)}
                            className="p-1 rounded hover:bg-indigo-50 text-indigo-600"
                            title="Ver detalle"
                            data-testid={`btn-detalle-${it.unidad_id}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => onOpenPago(it)}
                            disabled={!saldoPos}
                            className="p-1 rounded hover:bg-emerald-50 text-emerald-600 disabled:opacity-30 disabled:cursor-not-allowed"
                            title={saldoPos ? 'Registrar pago' : 'Sin saldo pendiente'}
                            data-testid={`btn-pago-${it.unidad_id}`}
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200 text-xs font-semibold sticky bottom-0 z-10">
                <tr>
                  <td colSpan={2} className="px-2 py-2 text-slate-600">Totales ({filtered.length})</td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmtMoney(filtered.reduce((s, it) => s + Number(it.monto_liquidado || 0), 0))}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmtMoney(filtered.reduce((s, it) => s + Number(it.deuda_anterior || 0), 0))}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmtMoney(filtered.reduce((s, it) => s + Number(it.total_a_cobrar || 0), 0))}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-emerald-700">{fmtMoney(filtered.reduce((s, it) => s + Number(it.total_cobrado || 0), 0))}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-rose-700">{fmtMoney(filtered.reduce((s, it) => s + Number(it.saldo_pendiente || 0), 0))}</td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      <RegistrarPagoDialog
        open={pagoDialog.open}
        onClose={onClosePago}
        edificioId={edificioId}
        periodoId={periodo?.periodo_id}
        cobro={pagoDialog.cobro}
        onSaved={loadData}
      />
      <CobroDetalleDialog
        open={detalleDialog.open}
        onClose={onCloseDetalle}
        edificioId={edificioId}
        periodoId={periodo?.periodo_id}
        unidadId={detalleDialog.unidadId}
        propietarioNombre={detalleDialog.propNombre}
        propietarios={detalleDialog.propietarios}
        onChanged={loadData}
      />
    </div>
  );
}
